/* eslint-disable react/prop-types */
/* eslint-disable react/sort-comp */
import { createRef, PureComponent } from 'react';
import api from '@shared/api';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import routes from '@root/routes';
import { isEmpty } from 'lodash/fp';
import { withTranslation } from 'react-i18next';
import { Alert, Steps, Row, Col, notification } from 'antd';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import Container from '@components/shared/Admin/Container';
import { matchType } from '@propTypes';
import { QueryStringTabs, TabPane } from '@components/shared/DeprecatedTabs';
import ListingMediaTable from './ListingMediaTable';
import ListingRooms from './ListingRooms';
import ListingForm from './ListingForm';
import ListingContacts from './ListingContacts';
import AskingRentForm from './AskingRentForm';
import Amenities from './Amenities';
import DataCollectionListingForm from './DataCollection';
import s from './ListingFormContainer.module.less';

const {
  api: {
    admin: { listing: apiAdminListingRoute },
  },
  admin: { building: adminBuildingRoute },
} = routes;

const TABS = ['form', 'amenities', 'dataCollection', 'media', 'rooms', 'contacts', 'rent'];
export class RawListingFormContainer extends PureComponent {
  listingForm = createRef();

  static propTypes = {
    flags: PropTypes.shape({
      'amenities-data-tabs': PropTypes.bool,
    }).isRequired,
    t: PropTypes.func.isRequired,
    match: matchType.isRequired,
    refreshRequestedListingsCount: PropTypes.func.isRequired,
  };

  state = {
    errors: null,
  };

  componentDidMount() {
    this.fetchListing();
  }

  setListingState(listing) {
    this.setState(state => ({
      ...state,
      listing,
    }));
  }

  formatNotificationErrorMessages = messages => (
    <div>
      {messages.map(message => (
        <p className={s.notificationErrorMessage} key={message}>
          {message}
        </p>
      ))}
    </div>
  );

  updateListing = async (listingId, body, message, successCallback = () => {}) => {
    const { refreshRequestedListingsCount } = this.props;
    const resp = await api.put(apiAdminListingRoute(listingId), body);
    const json = await resp.json();

    if (resp.ok) {
      this.setListingState(json);
      refreshRequestedListingsCount();
      this.setState({ errors: null });
      successCallback();
      notification.success({ message });
    } else if (!isEmpty(json.errors)) {
      this.setState({ errors: json.errors }, () => {
        this.listingForm.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } else {
      notification.error({ message: this.formatNotificationErrorMessages(json.messages) });
    }
  };

  fetchListing = async () => {
    const { match } = this.props;
    const {
      params: { id: listingId },
    } = match;

    const resp = await api.fetch(apiAdminListingRoute(listingId));
    const listing = await resp.json();
    this.setListingState(listing);
  };

  errorsMessage = () => {
    const { listing, errors } = this.state;
    const { t } = this.props;

    return errors.map(error => {
      if (error.field === 'status') {
        return (
          <div key={error.field}>
            <div className={s.errorMessage}>{error.message}</div>
            <Link
              className={s.viewBuildingDataText}
              to={adminBuildingRoute(listing.building.id)}
              target="_blank"
            >
              {t('listing.viewBuildingData')}
            </Link>
          </div>
        );
      }
      return (
        <div key={error.field} className={s.errorMessage}>
          {error.message}
        </div>
      );
    });
  };

  render() {
    const { listing, errors } = this.state;
    const { t, flags } = this.props;

    if (!listing) return null;

    const hasErrors = !isEmpty(errors);
    const errorMessage = hasErrors ? this.errorsMessage() : null;

    return (
      <div ref={this.listingForm} className={s.listingForm}>
        <Row>
          <Col span={12}>
            <Steps current={1}>
              <Steps.Step
                title={t('listing.steps.search.title')}
                description={t('listing.steps.search.description')}
              />
              <Steps.Step
                title={t('listing.steps.edit.title')}
                description={t('listing.steps.edit.description')}
              />
            </Steps>
          </Col>
        </Row>
        <Container>
          <QueryStringTabs tabs={TABS}>
            <TabPane key="form" tab={t(`listing.menuItems.form`)}>
              {hasErrors ? <Alert showIcon type="error" description={errorMessage} /> : null}
              <ListingForm
                fetchListing={this.fetchListing}
                listing={listing}
                updateListing={this.updateListing}
              />
            </TabPane>
            {flags['amenities-data-tabs'] ? (
              <TabPane key="amenities" tab={t(`listing.menuItems.amenities`)}>
                <Amenities listing={listing} updateListing={this.updateListing} />
              </TabPane>
            ) : null}
            {flags['amenities-data-tabs'] ? (
              <TabPane key="dataCollection" tab={t(`listing.menuItems.dataCollection`)}>
                <DataCollectionListingForm listing={listing} updateListing={this.updateListing} />
              </TabPane>
            ) : null}
            <TabPane key="media" tab={t(`listing.menuItems.media`)}>
              <ListingMediaTable
                fetchListing={this.fetchListing}
                contentShootRequest={listing.contentShootRequest}
                photos={listing.photos}
                video={listing.video}
                rooms={listing.rooms}
                googleDriveMediaDirectoryId={listing.googleDriveMediaDirectoryId}
                googleDriveMediaDirectoryIdTwo={listing.googleDriveMediaDirectoryIdTwo}
                listingId={listing.id}
                tourEmbedUrl={listing.tourEmbedUrl}
                formatNotificationErrorMessages={this.formatNotificationErrorMessages}
                spaceFloorPlanPhotos={listing.space.floorPlanPhotos}
                additionalPhotos={listing.space.additionalPhotos}
                spaceBrochure={listing.space.brochure}
                buildingBrochure={listing.building.brochure}
                floorPlan={listing.floorPlan}
                virtualTourSpotlight={listing.virtualTourSpotlight}
              />
            </TabPane>
            <TabPane key="rooms" tab={t(`listing.menuItems.rooms`)}>
              <ListingRooms
                listingId={listing.id}
                rooms={listing.rooms}
                fetchListing={this.fetchListing}
              />
            </TabPane>
            <TabPane key="contacts" tab={t(`listing.menuItems.contacts`)}>
              <ListingContacts
                data={listing.listingAgents}
                editable={!listing.usingBuildingContacts}
                onSort={this.fetchListing}
                buildingId={listing.building.id}
              />
            </TabPane>
            <TabPane key="rent" tab={t(`listing.menuItems.rent`)}>
              <AskingRentForm
                fetchListing={this.fetchListing}
                listingId={listing.id}
                currency={listing.building.currency}
                units={listing.building.units}
                rents={listing.baseRents}
                visibility={listing.isListingPriceVisible}
              />
            </TabPane>
          </QueryStringTabs>
        </Container>
      </div>
    );
  }
}

const EnhancedListingFormContainer = withLDConsumer()(
  withTranslation('admin')(RawListingFormContainer),
);
export default EnhancedListingFormContainer;
